import React from 'react';
import { Box, Typography, Container, Grid, Link, Divider, IconButton, Stack } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const accent = '#A67C52';

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'About Us', href: '/about' },
  { label: 'What We Do', href: '/services' },
  { label: 'Export Countries', href: '/countries' },
  { label: 'Contact', href: '/contact' },
];

const socials = [
  { icon: <LinkedInIcon />, href: 'https://www.linkedin.com/', label: 'LinkedIn' },
  { icon: <InstagramIcon />, href: 'https://www.instagram.com/', label: 'Instagram' },
  { icon: <WhatsAppIcon />, href: 'https://wa.me/+971 547452077', label: 'WhatsApp' },
];

const Footer = () => {

  return (
    <Box sx={{
      bgcolor: '#181818',
      color: '#f5f5f5',
      pt: { xs: 0, md: 0 },
      pb: { xs: 2, md: 4 },
      width: '100%',
      position: 'relative',
      mt: 0,
    }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Grid container spacing={{ xs: 3, md: 6 }} justifyContent="center" alignItems="flex-start" sx={{ mb: 2 }}>
          {/* Company Info & Socials */}
          <Grid item xs={12} md={4} sx={{ mb: { xs: 2, md: 0 }, pr: { md: 4 }, textAlign: { xs: 'center', md: 'left' } }}>
            <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '0.08em', mb: 1.2, color: accent, fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
              DigiWood FZCO
            </Typography>
            <Typography variant="body2" sx={{ color: accent, opacity: 0.85, fontSize: { xs: '1.05rem', md: '1.12rem' }, mb: 1.2 }}>
              FSC-Certified Timber Exporters from Gabon to the World
            </Typography>
            <Typography variant="body2" sx={{ color: '#f5f5f5', opacity: 0.85, fontSize: { xs: '0.98rem', md: '1.05rem' }, mb: 2, fontStyle: 'italic' }}>
              "Sustainably connecting Africa’s forests to the world."
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ mt: 1 }}>
              {socials.map((s) => (
                <IconButton
                  key={s.label}
                  component="a"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  sx={{ color: accent, bgcolor: 'rgba(255,255,255,0.04)', '&:hover': { bgcolor: accent, color: '#fff' }, transition: 'all 0.2s' }}
                >
                  {s.icon}
                </IconButton>
              ))}
            </Stack>
          </Grid>
          {/* Divider for mobile */}
          <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' }, my: 1 }}>
            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
          </Grid>
          {/* Quick Links */}
          <Grid item xs={12} md={4} sx={{ mb: { xs: 2, md: 0 }, textAlign: { xs: 'center', md: 'center' }, px: { md: 2 } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.7, color: accent, letterSpacing: '0.04em', fontSize: { xs: '1.08rem', md: '1.18rem' }, textTransform: 'uppercase' }}>
              Quick Links
            </Typography>
            <Divider sx={{ bgcolor: accent, width: 40, mx: 'auto', my: 1, borderBottomWidth: 2, borderRadius: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row', md: 'column' }, alignItems: { xs: 'center', sm: 'flex-start', md: 'center' }, justifyContent: 'center', gap: { xs: 0.5, sm: 2, md: 0.5 } }}>
              {quickLinks.map((link) => (
                <Box key={link.label} sx={{ mb: { xs: 0.7, md: 1.1 }, width: { xs: '100%', sm: 'auto' } }}>
                  <Link
                    href={link.href}
                    underline="none"
                    sx={{
                      color: accent,
                      fontWeight: 600,
                      fontSize: { xs: '1.05rem', md: '1.12rem' },
                      transition: 'color 0.2s, border-bottom 0.2s',
                      borderBottom: '2px solid transparent',
                      '&:hover': { color: 'primary.main', borderBottom: `2px solid ${accent}`, bgcolor: 'rgba(166,124,82,0.08)', borderRadius: 2 },
                      cursor: 'pointer',
                      display: 'inline-block',
                      mb: 0.2,
                      pb: 0.1,
                      px: 1,
                    }}
                  >
                    {link.label}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>
          {/* Divider for mobile */}
          <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' }, my: 1 }}>
            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
          </Grid>
          {/* Contact Info */}
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', md: 'left' }, display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' }, pl: { md: 4 } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.2, color: accent, letterSpacing: '0.04em', fontSize: { xs: '1.08rem', md: '1.18rem' }, textTransform: 'uppercase' }}>
              Contact
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.7 }}>
              <EmailIcon sx={{ color: accent, fontSize: 20 }} />
              <Link href="mailto:digiwooddubai@gmail.com" sx={{ color: accent, fontWeight: 600, fontSize: { xs: '0.98rem', md: '1.05rem' }, textDecoration: 'none', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>digiwooddubai@gmail.com</Link>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.7 }}>
              <PhoneIcon sx={{ color: accent, fontSize: 20 }} />
              <Link href="tel:+971547452077" sx={{ color: accent, fontWeight: 600, fontSize: { xs: '0.98rem', md: '1.05rem' }, textDecoration: 'none', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>+971 547452077</Link>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.7 }}>
              <LocationOnIcon sx={{ color: accent, fontSize: 20, mt: 0.3 }} />
              <Typography variant="body2" sx={{ color: accent, opacity: 0.85, fontSize: { xs: '0.98rem', md: '1.05rem' }, lineHeight: 1.7, textAlign: 'left' }}>
                6WA,507, 5Th Floor<br />
                Dubai Airport Freezone<br />
                Dubai - United Arab Emirates
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.12)', my: { xs: 1.5, md: 2.5 } }} />
        <Typography variant="body2" align="center" sx={{ color: accent, opacity: 0.7, fontSize: { xs: '0.97rem', md: '1.03rem' }, mt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.7 }}>
          © 2025 DigiWood FZCO. All rights reserved. <span style={{ fontSize: '1.1em', color: accent, marginLeft: 4 }}>🌳</span>
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer; 